import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Copy, Calendar } from "lucide-react";

export default function Library() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(null); // item

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/library");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/library/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error("Delete failed."); }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied.");
  };

  const schedule = async (it, isoDatetime) => {
    try {
      await api.post(`/library/${it.id}/schedule`, { scheduled_at: isoDatetime });
      setItems((p) => p.map((i) => (i.id === it.id ? { ...i, scheduled_at: isoDatetime } : i)));
      setScheduleOpen(null);
      toast.success(isoDatetime ? "Scheduled." : "Unscheduled.");
    } catch (e) { toast.error("Schedule failed."); }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  const filters = [
    { v: "all", l: "All" },
    { v: "hook", l: "Hooks" },
    { v: "remix", l: "Remixes" },
    { v: "score", l: "Scores" },
  ];

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-8">
      <header className="border-b border-white/10 pb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow">// module 04</div>
          <h1 className="heading-display text-4xl sm:text-5xl mt-2">THE <span className="text-signal-yellow">VAULT</span></h1>
          <p className="font-mono text-xs text-zinc-500 mt-3">// every transmission you saved. searchable. exportable. yours.</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="library-filters">
          {filters.map((f) => (
            <button
              key={f.v}
              data-testid={`filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-3 py-2 border font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                filter === f.v
                  ? "bg-signal-yellow text-black border-signal-yellow"
                  : "bg-transparent text-zinc-400 border-white/15 hover:border-white/40"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="font-mono text-xs text-zinc-500 animate-pulse">// loading vault...</div>
      ) : filtered.length === 0 ? (
        <div className="brutal-border p-12 text-center bg-[#141414]">
          <div className="font-mono text-xs text-zinc-500">// vault empty. start generating to fill it up.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => (
            <article key={it.id} data-testid={`vault-item-${it.id}`} className="brutal-border bg-[#141414] p-5 space-y-3 hover:border-white/30 transition-colors">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[10px] bg-signal-yellow text-black px-2 py-1 uppercase tracking-wider">{it.type}</span>
                  <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{it.platform}</span>
                  {it.score != null && <span className="font-mono text-[10px] text-signal-yellow">{it.score}/100</span>}
                  <span className="font-mono text-[10px] text-zinc-600">{new Date(it.created_at).toLocaleString()}</span>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`schedule-${it.id}`} onClick={() => setScheduleOpen(it)} className="border border-white/15 hover:border-signal-yellow hover:text-signal-yellow text-zinc-400 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors">
                    <Calendar size={11} className="inline mr-1.5" />{it.scheduled_at ? new Date(it.scheduled_at).toLocaleDateString() : "Schedule"}
                  </button>
                  <button data-testid={`copy-${it.id}`} onClick={() => copy(it.output)} className="border border-white/15 hover:border-white/40 text-zinc-400 hover:text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors">
                    <Copy size={11} className="inline mr-1.5" />Copy
                  </button>
                  <button data-testid={`delete-${it.id}`} onClick={() => del(it.id)} className="border border-white/15 hover:border-signal-red text-zinc-400 hover:text-signal-red px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors">
                    <Trash2 size={11} className="inline mr-1.5" />Delete
                  </button>
                </div>
              </div>
              <div className="font-mono text-[11px] text-zinc-500 truncate">// input: {it.input}</div>
              <pre className="font-mono text-xs text-zinc-200 whitespace-pre-wrap break-words border-l-2 border-signal-yellow pl-4 max-h-48 overflow-auto">
                {it.type === "score" ? formatScore(it.output) : it.output}
              </pre>
            </article>
          ))}
        </div>
      )}

      {scheduleOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setScheduleOpen(null)}>
          <div className="brutal-border-strong bg-[#141414] p-6 max-w-md w-full space-y-4" data-testid="schedule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow">// schedule</div>
            <h3 className="heading-display text-2xl">SCHEDULE THIS POST</h3>
            <input
              data-testid="schedule-datetime"
              type="datetime-local"
              defaultValue={scheduleOpen.scheduled_at ? scheduleOpen.scheduled_at.slice(0, 16) : new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16)}
              onChange={(e) => (scheduleOpen._pending = e.target.value)}
              className="w-full bg-black border border-white/10 text-white p-3 font-mono text-sm focus:border-signal-yellow focus:outline-none"
            />
            <div className="flex gap-2 justify-end">
              {scheduleOpen.scheduled_at && (
                <button data-testid="schedule-clear" onClick={() => schedule(scheduleOpen, null)} className="text-signal-red font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2">
                  Unschedule
                </button>
              )}
              <button onClick={() => setScheduleOpen(null)} className="border border-white/15 hover:border-white/40 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors">Cancel</button>
              <button
                data-testid="schedule-save"
                onClick={() => {
                  const val = scheduleOpen._pending || new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16);
                  schedule(scheduleOpen, new Date(val).toISOString());
                }}
                className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2 hover:bg-signal-yellow-hover transition-colors"
              >Save →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatScore(raw) {
  try {
    const d = JSON.parse(raw);
    const parts = [`Score: ${d.score}/100`];
    if (d.verdict) parts.push(`Verdict: ${d.verdict}`);
    if (d.fixes?.length) parts.push(`Fixes:\n- ${d.fixes.join("\n- ")}`);
    return parts.join("\n\n");
  } catch { return raw; }
}
